import { apiService } from './api';
import { 
  PagedResult, 
  UserListDto, 
  UserDetailDto,
  MonitoredIdentityDto,
  UpdateUserStatusDto,
  UpdateAdminPrivilegesDto,
  EmailQueueStatsDto,
  EmailQueueListItemDto,
  EmailQueueDto
} from '../types/admin';

export interface UserListQuery {
  page?: number;
  pageSize?: number;
  sortBy?: 'email' | 'fullName' | 'createdAt';
  sortDescending?: boolean;
}

class AdminService {
  /**
   * Get a paginated list of all registered users
   * @param query Pagination and sorting parameters
   * @param token JWT bearer token for authentication
   * @returns Paginated list of users
   * @throws Error if the request fails
   */
  async getUsers(
    query: UserListQuery = {},
    token: string
  ): Promise<PagedResult<UserListDto>> {
    const page = query.page || 1;
    const pageSize = query.pageSize || 50;
    const sortBy = query.sortBy || 'createdAt';
    const sortDescending = query.sortDescending ?? true;

    return apiService.getAdminUsers(token, page, pageSize, sortBy, sortDescending);
  }

  /**
   * Get detailed information about a specific user
   * @param userId The unique identifier of the user
   * @param token JWT bearer token for authentication
   * @returns Detailed user information
   * @throws Error if the request fails
   */
  async getUserById(userId: string, token: string): Promise<UserDetailDto> {
    return apiService.getAdminUserById(userId, token);
  }

  /**
   * Get paginated list of monitored identities for a specific user
   * @param userId The unique identifier of the user
   * @param page Page number (1-based)
   * @param pageSize Number of items per page
   * @param token JWT bearer token for authentication
   * @returns Paginated list of monitored identities with mention counts
   * @throws Error if the request fails
   */
  async getUserMonitoredIdentities(
    userId: string,
    page: number,
    pageSize: number,
    token: string
  ): Promise<PagedResult<MonitoredIdentityDto>> {
    return apiService.getAdminUserMonitoredIdentities(userId, page, pageSize, token);
  }

  /**
   * Update user status (enable/disable)
   * @param userId The unique identifier of the user
   * @param dto Status update data
   * @param token JWT bearer token for authentication
   * @returns Updated user details
   * @throws Error if the request fails
   */
  async updateUserStatus(
    userId: string,
    dto: UpdateUserStatusDto,
    token: string
  ): Promise<UserDetailDto> {
    return apiService.updateAdminUserStatus(userId, dto, token);
  }

  /**
   * Update user admin privileges (grant/revoke)
   * @param userId The unique identifier of the user
   * @param dto Admin privileges update data
   * @param token JWT bearer token for authentication
   * @returns Updated user details
   * @throws Error if the request fails
   */
  async updateAdminPrivileges(
    userId: string,
    dto: UpdateAdminPrivilegesDto,
    token: string
  ): Promise<UserDetailDto> {
    return apiService.updateAdminUserPrivileges(userId, dto, token);
  }

  // Email Queue Methods (T051)

  /**
   * Get email queue statistics
   * @param token JWT bearer token for authentication
   * @returns Queue statistics with counts by status
   * @throws Error if the request fails
   */
  async getEmailQueueStats(token: string): Promise<EmailQueueStatsDto> {
    return apiService.getAdminEmailQueueStats(token);
  }

  /**
   * Get paginated list of emails in the queue
   * @param query Pagination and filtering parameters
   * @param token JWT bearer token for authentication
   * @returns Paginated list of emails
   * @throws Error if the request fails
   */
  async getEmailQueue(
    query: {
      pageNumber?: number;
      pageSize?: number;
      status?: string;
      emailType?: string;
      recipientEmail?: string;
      sortBy?: string;
      sortDescending?: boolean;
    } = {},
    token: string
  ): Promise<PagedResult<EmailQueueListItemDto>> {
    return apiService.getAdminEmailQueue(query, token);
  }

  /**
   * Get detailed information for a specific email
   * @param id Email queue ID
   * @param token JWT bearer token for authentication
   * @returns Email details including body, error history, and metadata
   * @throws Error if the request fails
   */
  async getEmailQueueById(id: number, token: string): Promise<EmailQueueDto> {
    return apiService.getAdminEmailQueueById(id, token);
  }

  /**
   * Manually retry a specific email
   * @param id Email queue ID
   * @param token JWT bearer token for authentication
   * @returns Updated email details
   * @throws Error if the request fails
   */
  async retryEmail(id: number, token: string): Promise<EmailQueueDto> {
    return apiService.retryAdminEmail(id, token);
  }

  /**
   * Delete an email from the queue
   * @param id Email queue ID
   * @param token JWT bearer token for authentication
   * @throws Error if the request fails
   */
  async deleteEmail(id: number, token: string): Promise<void> {
    return apiService.deleteAdminEmail(id, token);
  }

  /**
   * Manually trigger the email queue processor
   * @param token JWT bearer token for authentication
   * @throws Error if the request fails
   */
  async triggerProcessing(token: string): Promise<void> {
    return apiService.triggerAdminEmailProcessing(token);
  }
}

export const adminService = new AdminService();
export default adminService;
